import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Search, FileDown, Copy, ChevronLeft, ChevronRight, Download, Eye } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { reportService, FixedAssetReportEntry } from '@/services/reportService'
import { formatCurrency } from '@/lib/format'

export const FixedAssetReport: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const { user } = useAuthStore()

  const [assetData, setAssetData] = useState<FixedAssetReportEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [totals, setTotals] = useState({
    totalPurchase: 0,
    totalAccumulatedDepreciation: 0,
    totalNetBookValue: 0,
  })

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    assetNo: true,
    assetName: true,
    category: true,
    project: true,
    location: true,
    purchasePrice: true,
    purchaseDate: true,
    lifeYears: true,
    lifeMonths: true,
    accumulatedDepreciation: true,
    openingBookValue: true,
    additions: true,
    disposals: true,
    ytdDepreciation: true,
    netBookValue: true,
  })

  // Get branch ID from user's selected branch
  const selectedBranchId = user?.selectedBranch?.branchId

  // Set default dates (current month)
  useEffect(() => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    
    setFromDate(formatDateForInput(firstDay))
    setToDate(formatDateForInput(lastDay))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return formatCurrency(Number(amount))
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!fromDate || !toDate) {
      toast.error('Please select both from date and to date')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const response = await reportService.getFixedAssetReport({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
      })
      setAssetData(response.data || [])
      setTotalCount(response.totalRecords || 0)
      setTotals({
        totalPurchase: response.totalPurchase || 0,
        totalAccumulatedDepreciation: response.totalAccumulatedDepreciation || 0,
        totalNetBookValue: response.totalNetBookValue || 0,
      })
      const pages = response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load fixed asset report')
      setAssetData([])
      setTotalCount(0)
      setTotalPages(0)
      setTotals({
        totalPurchase: 0,
        totalAccumulatedDepreciation: 0,
        totalNetBookValue: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return assetData
    const query = searchQuery.toLowerCase()
    return assetData.filter((entry) => {
      return (
        (entry.assetNo || '').toLowerCase().includes(query) ||
        (entry.assetName || '').toLowerCase().includes(query) ||
        (entry.category || '').toLowerCase().includes(query) ||
        (entry.project || '').toLowerCase().includes(query) ||
        (entry.costLocation || '').toLowerCase().includes(query) ||
        (entry.costCenter || '').toLowerCase().includes(query)
      )
    })
  }, [assetData, searchQuery])

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && fromDate && toDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && fromDate && toDate && pageNumber > 1 && assetData.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const handleDownload = async () => {
    if (!selectedBranchId || !fromDate || !toDate) {
      toast.error('Please select date range before downloading')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      const blob = await reportService.downloadFixedAssetReportExcel({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
      })
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
      link.download = `fixed-asset-report-${dateStr}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Excel file downloaded successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download Excel file')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    if (filteredData.length === 0) {
      toast.error('No data to copy')
      return
    }

    // Create CSV content
    const headers = [
      t('fixedAssets.assetNo'),
      t('fixedAssets.assetName'),
      t('fixedAssets.category'),
      t('fixedAssets.project'),
      t('fixedAssets.location'),
      t('fixedAssets.purchasePrice'),
      t('fixedAssets.purchaseDate'),
      t('fixedAssets.lifeYears'),
      t('fixedAssets.lifeMonths'),
      t('fixedAssets.accumulatedDepreciation'),
      t('fixedAssets.openingBookValue'),
      t('fixedAssets.additions'),
      t('fixedAssets.disposals'),
      t('fixedAssets.ytdDepreciation'),
      t('fixedAssets.netBookValue'),
    ].join('\t')

    const rows = filteredData.map(entry => [
      entry.assetNo || '',
      entry.assetName || '',
      entry.category || '',
      entry.project || '',
      entry.costLocation || '',
      formatAmount(entry.purchasePrice),
      formatDate(entry.purchaseDate),
      entry.lifeYears || '',
      entry.lifeMonths || '',
      formatAmount(entry.accumulatedDepreciation),
      formatAmount(entry.openingBookValue),
      formatAmount(entry.additions),
      formatAmount(entry.disposals),
      formatAmount(entry.ytdDepreciation),
      formatAmount(entry.netBookValue),
    ].join('\t'))

    const csvContent = [headers, ...rows].join('\n')
    navigator.clipboard.writeText(csvContent).then(() => {
      toast.success('Data copied to clipboard')
    }).catch(() => toast.error('Failed to copy data'))
  }

  const handleCsv = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      t('fixedAssets.assetNo'),
      t('fixedAssets.assetName'),
      t('fixedAssets.category'),
      t('fixedAssets.project'),
      t('fixedAssets.location'),
      t('fixedAssets.purchasePrice'),
      t('fixedAssets.purchaseDate'),
      t('fixedAssets.lifeYears'),
      t('fixedAssets.lifeMonths'),
      t('fixedAssets.accumulatedDepreciation'),
      t('fixedAssets.openingBookValue'),
      t('fixedAssets.additions'),
      t('fixedAssets.disposals'),
      t('fixedAssets.ytdDepreciation'),
      t('fixedAssets.netBookValue'),
    ].join(',')

    const rows = filteredData.map(entry => [
      `"${entry.assetNo || ''}"`,
      `"${entry.assetName || ''}"`,
      `"${entry.category || ''}"`,
      `"${entry.project || ''}"`,
      `"${entry.costLocation || ''}"`,
      formatAmount(entry.purchasePrice),
      `"${formatDate(entry.purchaseDate)}"`,
      entry.lifeYears || '',
      entry.lifeMonths || '',
      formatAmount(entry.accumulatedDepreciation),
      formatAmount(entry.openingBookValue),
      formatAmount(entry.additions),
      formatAmount(entry.disposals),
      formatAmount(entry.ytdDepreciation),
      formatAmount(entry.netBookValue),
    ].join(','))

    // Add total row
    const totalRow = [
      '"Total"',
      '""',
      '""',
      '""',
      '""',
      formatAmount(totals.totalPurchase),
      '""',
      '""',
      '""',
      formatAmount(totals.totalAccumulatedDepreciation),
      '""',
      '""',
      '""',
      '""',
      formatAmount(totals.totalNetBookValue),
    ].join(',')

    const csvContent = [headers, ...rows, totalRow].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
    link.setAttribute('download', `fixed-asset-report-${dateStr}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.fixedAssetReport')}
        description={t('fixedAssets.viewFixedAssetReport')}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <Button variant="outline" onClick={handleDownload} disabled={loading}>
                <Download className="h-4 w-4 mr-2" />
                {t('reports.download')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load fixed asset report"
              />
            </div>
          ) : (
            <>
              {/* Table Controls */}
              <div className="border-b px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex-1 w-full">
                  <Input
                    placeholder={t('reports.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-2" />
                        Column Visibility
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.assetNo}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, assetNo: checked })}
                      >
                        {t('fixedAssets.assetNo')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.assetName}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, assetName: checked })}
                      >
                        {t('fixedAssets.assetName')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.category}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, category: checked })}
                      >
                        {t('fixedAssets.category')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.project}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, project: checked })}
                      >
                        {t('fixedAssets.project')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.location}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, location: checked })}
                      >
                        {t('fixedAssets.location')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.purchasePrice}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, purchasePrice: checked })}
                      >
                        {t('fixedAssets.purchasePrice')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.purchaseDate}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, purchaseDate: checked })}
                      >
                        {t('fixedAssets.purchaseDate')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.lifeYears}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, lifeYears: checked })}
                      >
                        {t('fixedAssets.lifeYears')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.lifeMonths}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, lifeMonths: checked })}
                      >
                        {t('fixedAssets.lifeMonths')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.accumulatedDepreciation}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, accumulatedDepreciation: checked })}
                      >
                        {t('fixedAssets.accumulatedDepreciation')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.openingBookValue}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, openingBookValue: checked })}
                      >
                        {t('fixedAssets.openingBookValue')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.additions}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, additions: checked })}
                      >
                        {t('fixedAssets.additions')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.disposals}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, disposals: checked })}
                      >
                        {t('fixedAssets.disposals')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.ytdDepreciation}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, ytdDepreciation: checked })}
                      >
                        {t('fixedAssets.ytdDepreciation')}
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuCheckboxItem
                        checked={visibleColumns.netBookValue}
                        onCheckedChange={(checked) => setVisibleColumns({ ...visibleColumns, netBookValue: checked })}
                      >
                        {t('fixedAssets.netBookValue')}
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="outline" size="sm" onClick={handleCsv}>
                    <FileDown className="h-4 w-4 mr-2" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow className="border-b-2 border-gray-300 bg-primary/5">
                      {visibleColumns.assetNo && (
                        <TableHead className={`font-bold text-primary  ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('fixedAssets.assetNo')}</TableHead>
                      )}
                      {visibleColumns.assetName && (
                        <TableHead className={`font-bold text-primary  ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('fixedAssets.assetName')}</TableHead>
                      )}
                      {visibleColumns.category && (
                        <TableHead className={`font-bold text-primary  ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('fixedAssets.category')}</TableHead>
                      )}
                      {visibleColumns.project && (
                        <TableHead className={`font-bold text-primary  ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('fixedAssets.project')}</TableHead>
                      )}
                      {visibleColumns.location && (
                        <TableHead className={`font-bold text-primary  ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('fixedAssets.location')}</TableHead>
                      )}
                      {visibleColumns.purchasePrice && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.purchasePrice')}</TableHead>
                      )}
                      {visibleColumns.purchaseDate && (
                        <TableHead className={`font-bold text-primary  ${rtl ? 'text-right pr-3' : 'text-left pl-3'}`}>{t('fixedAssets.purchaseDate')}</TableHead>
                      )}
                      {visibleColumns.lifeYears && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.lifeYears')}</TableHead>
                      )}
                      {visibleColumns.lifeMonths && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.lifeMonths')}</TableHead>
                      )}
                      {visibleColumns.accumulatedDepreciation && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.accumulatedDepreciation')}</TableHead>
                      )}
                      {visibleColumns.openingBookValue && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.openingBookValue')}</TableHead>
                      )}
                      {visibleColumns.additions && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.additions')}</TableHead>
                      )}
                      {visibleColumns.disposals && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.disposals')}</TableHead>
                      )}
                      {visibleColumns.ytdDepreciation && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.ytdDepreciation')}</TableHead>
                      )}
                      {visibleColumns.netBookValue && (
                        <TableHead className="font-bold text-primary text-right  pr-3">{t('fixedAssets.netBookValue')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((entry, index) => (
                      <TableRow
                        key={index}
                        className="border-b border-gray-300 hover:bg-primary/5 transition-colors"
                      >
                        {visibleColumns.assetNo && (
                          <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>{entry.assetNo || '-'}</TableCell>
                        )}
                        {visibleColumns.assetName && (
                          <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>{entry.assetName || '-'}</TableCell>
                        )}
                        {visibleColumns.category && (
                          <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>{entry.category || '-'}</TableCell>
                        )}
                        {visibleColumns.project && (
                          <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>{entry.project || '-'}</TableCell>
                        )}
                        {visibleColumns.location && (
                          <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>{entry.costLocation || '-'}</TableCell>
                        )}
                        {visibleColumns.purchasePrice && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.purchasePrice)}</TableCell>
                        )}
                        {visibleColumns.purchaseDate && (
                          <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>{formatDate(entry.purchaseDate)}</TableCell>
                        )}
                        {visibleColumns.lifeYears && (
                          <TableCell className="text-right px-3 py-3 ">{entry.lifeYears || '-'}</TableCell>
                        )}
                        {visibleColumns.lifeMonths && (
                          <TableCell className="text-right px-3 py-3 ">{entry.lifeMonths || '-'}</TableCell>
                        )}
                        {visibleColumns.accumulatedDepreciation && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.accumulatedDepreciation)}</TableCell>
                        )}
                        {visibleColumns.openingBookValue && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.openingBookValue)}</TableCell>
                        )}
                        {visibleColumns.additions && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.additions)}</TableCell>
                        )}
                        {visibleColumns.disposals && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.disposals)}</TableCell>
                        )}
                        {visibleColumns.ytdDepreciation && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.ytdDepreciation)}</TableCell>
                        )}
                        {visibleColumns.netBookValue && (
                          <TableCell className="text-right px-3 py-3 ">{formatAmount(entry.netBookValue)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow className="border-t-2 border-gray-300 bg-primary/5 font-bold">
                      {visibleColumns.assetNo && (
                        <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}></TableCell>
                      )}
                      {visibleColumns.assetName && (
                        <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}></TableCell>
                      )}
                      {visibleColumns.category && (
                        <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}></TableCell>
                      )}
                      {visibleColumns.project && (
                        <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}></TableCell>
                      )}
                      {visibleColumns.location && (
                        <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}></TableCell>
                      )}
                      {visibleColumns.purchasePrice && (
                        <TableCell className="text-right px-3 py-3 ">{formatAmount(totals.totalPurchase)}</TableCell>
                      )}
                      {visibleColumns.purchaseDate && (
                        <TableCell className={`px-3 py-3  ${rtl ? 'text-right' : 'text-left'}`}>Total</TableCell>
                      )}
                      {visibleColumns.lifeYears && (
                        <TableCell className="text-right px-3 py-3 "></TableCell>
                      )}
                      {visibleColumns.lifeMonths && (
                        <TableCell className="text-right px-3 py-3 "></TableCell>
                      )}
                      {visibleColumns.accumulatedDepreciation && (
                        <TableCell className="text-right px-3 py-3 ">{formatAmount(totals.totalAccumulatedDepreciation)}</TableCell>
                      )}
                      {visibleColumns.openingBookValue && (
                        <TableCell className="text-right px-3 py-3 "></TableCell>
                      )}
                      {visibleColumns.additions && (
                        <TableCell className="text-right px-3 py-3 "></TableCell>
                      )}
                      {visibleColumns.disposals && (
                        <TableCell className="text-right px-3 py-3 "></TableCell>
                      )}
                      {visibleColumns.ytdDepreciation && (
                        <TableCell className="text-right px-3 py-3 "></TableCell>
                      )}
                      {visibleColumns.netBookValue && (
                        <TableCell className="text-right px-3 py-3 ">{formatAmount(totals.totalNetBookValue)}</TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

